import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Info } from "../../__generated__/graphql";
import { LoadingOverlay } from "../../components/Loading";
import { PageContainer } from "../../components/PageContainer";
import {
  CREATE_INFO,
  DELETE_INFO,
  GET_INFOS,
  UPDATE_INFO,
} from "./graphql";
import { UpdateMiscConfigButton } from "../links/updateMiscConfig";

type InfoRow = {
  id: string;
  title: string;
  order: number;
  hidden: boolean;
  value: any;
  headerColor?: string | null;
};

const normalizeOrder = (value?: number | null): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  return Math.max(0, value);
};

const reorderInfoRows = (
  items: InfoRow[],
  draggedId: string,
  targetId: string
): InfoRow[] => {
  const fromIndex = items.findIndex((item) => item.id === draggedId);
  const toIndex = items.findIndex((item) => item.id === targetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return items;
  }

  const reordered = [...items];
  const [dragged] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, dragged);

  return reordered.map((item, index) => ({
    ...item,
    order: index,
  }));
};

export const InfoList = ({
  infos,
  refetch,
}: {
  infos: Info[];
  refetch: () => void;
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState<InfoRow[]>([]);
  const [draggingInfoId, setDraggingInfoId] = React.useState<string | null>(
    null
  );
  const [dropTargetInfoId, setDropTargetInfoId] = React.useState<string | null>(
    null
  );
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);

  React.useEffect(() => {
    const nextRows = (infos ?? []).map((info) => ({
      id: info.id,
      title: info.title ?? "",
      order: normalizeOrder(info.order),
      hidden: Boolean(info.hidden),
      value: info.value,
      headerColor: info.headerColor ?? null,
    }));
    setRows(nextRows);
  }, [infos]);

  const originalOrderById = React.useMemo(() => {
    const map = new Map<string, number>();
    (infos ?? []).forEach((info) => {
      map.set(info.id, normalizeOrder(info.order));
    });
    return map;
  }, [infos]);

  const hasOrderChanges = React.useMemo(() => {
    return rows.some((row) => originalOrderById.get(row.id) !== row.order);
  }, [originalOrderById, rows]);

  const [createInfo] = useMutation(CREATE_INFO, {
    onCompleted: () => {
      refetch();
      toast("Create complete");
    },
  });

  const [deleteInfo] = useMutation(DELETE_INFO, {
    refetchQueries: [GET_INFOS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      refetch();
      toast("Delete complete");
    },
  });

  const [updateInfo] = useMutation(UPDATE_INFO);

  const handleCreateInfo = () => {
    const title = window.prompt("Section title");
    const trimmedTitle = title?.trim() ?? "";
    if (!trimmedTitle) return;

    createInfo({
      variables: {
        title: trimmedTitle,
        value: {
          items: [
            {
              infoType: "text",
              content: "",
              order: 0,
            },
          ],
        },
      },
    });
  };

  const handleSaveOrder = async () => {
    if (!hasOrderChanges || isSavingOrder) return;

    setIsSavingOrder(true);
    let updatedCount = 0;
    let firstError: string | null = null;

    for (const row of rows) {
      const previousOrder = originalOrderById.get(row.id);
      if (previousOrder === row.order) continue;

      try {
        await updateInfo({
          variables: {
            id: row.id,
            title: row.title,
            value: row.value,
            hidden: row.hidden,
            order: normalizeOrder(row.order),
            headerColor: row.headerColor ?? null,
          },
        });
        updatedCount += 1;
      } catch (err) {
        firstError =
          firstError ??
          (err instanceof Error ? err.message : "Failed to update info order");
      }
    }

    if (updatedCount > 0) {
      toast(
        updatedCount === 1
          ? "Info order updated"
          : `${updatedCount} info orders updated`
      );
      await refetch();
    }

    if (firstError) {
      toast.error(`Some info orders could not be saved: ${firstError}`);
    }

    setIsSavingOrder(false);
  };

  return (
    <PageContainer title="Information Sections">
      <LoadingOverlay loading={false}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Use this page to manage information sections. Each row is a section
          title, and its content is built from subsections on the edit screen.
        </Alert>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button onClick={handleCreateInfo} color="success" variant="contained">
            Add Information Section
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveOrder}
            disabled={!hasOrderChanges || isSavingOrder}
          >
            {isSavingOrder ? "Saving..." : "Save order"}
          </Button>
          <UpdateMiscConfigButton />
        </Box>

        <Box sx={{ mt: 1.5, color: "text.secondary", fontSize: 14 }}>
          Drag rows to control information section order in the app, then save
          changes.
        </Box>

        <TableContainer component={Paper} sx={{ mt: 2, overflow: "visible" }}>
          <Table sx={{ minWidth: 650 }} aria-label="info table">
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ width: 80 }}>
                  drag
                </TableCell>
                <TableCell align="left">Section title</TableCell>
                <TableCell align="left">Subsections</TableCell>
                <TableCell align="left">Visibility</TableCell>
                <TableCell align="left">actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  draggable
                  onDragStart={() => setDraggingInfoId(row.id)}
                  onDragEnd={() => {
                    setDraggingInfoId(null);
                    setDropTargetInfoId(null);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (dropTargetInfoId !== row.id) {
                      setDropTargetInfoId(row.id);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!draggingInfoId) return;

                    setRows((current) =>
                      reorderInfoRows(current, draggingInfoId, row.id)
                    );
                    setDraggingInfoId(null);
                    setDropTargetInfoId(null);
                  }}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  style={{
                    opacity: draggingInfoId === row.id ? 0.55 : 1,
                    backgroundColor:
                      dropTargetInfoId === row.id
                        ? "rgba(25, 118, 210, 0.08)"
                        : undefined,
                    cursor: "move",
                  }}
                >
                  <TableCell align="left">::</TableCell>
                  <TableCell align="left">
                    {row.title || "Untitled section"}
                  </TableCell>
                  <TableCell align="left">
                    {Array.isArray(row.value?.items) ? row.value.items.length : 0}
                  </TableCell>
                  <TableCell align="left">
                    {row.hidden ? "Hidden" : "Visible"}
                  </TableCell>
                  <TableCell align="left">
                    <Button
                      onClick={() => navigate(`/information/edit/${row.id}`)}
                      color="info"
                      variant="contained"
                      sx={{ mr: 1 }}
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => {
                        deleteInfo({ variables: { infoId: row.id } });
                      }}
                      color="error"
                      variant="contained"
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </LoadingOverlay>
    </PageContainer>
  );
};
